// 員工個資 / 人事資料 — 前端資料層 (V243 / employee-profile.ts)。
//   · useMyProfile / useUpdateMyProfile          — 本人自助 (① 基本個資)
//   · useMemberProfile / useUpdateMemberProfile  — 人資/系統管理員 (①②③)
// ③ 高敏感 (身分證/薪資帳號) 讀取只回遮罩 (mask)；寫入送明文、後端加密。
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ApiError, type DevActorPreset, api } from './api.ts';

export interface EmployeeProfile {
  user_id: string;
  // ① 基本個資
  birth_date: string | null;
  gender: 'male' | 'female' | 'other' | null;
  mobile_phone: string | null;
  home_phone: string | null;
  mailing_address: string | null;
  registered_address: string | null;
  emergency_contact_name: string | null;
  emergency_contact_relation: string | null;
  emergency_contact_phone: string | null;
  // ② 雇用資訊
  hire_date: string | null;
  termination_date: string | null;
  termination_reason: string | null;
  department: string | null;
  manager_user_id: string | null;
  /** V376: 合夥人 / 顧問等非受僱身分 —— 缺件檢查不適用（到職日仍照留）。 */
  doc_exempt: boolean;
  doc_exempt_reason: string | null;
  manager_name: string | null; // 直屬主管顯示名(唯讀)
  employee_name: string | null; // 員工本人姓名(唯讀,證明用)
  title_name: string | null; // 職稱(唯讀,證明用)
  // ③ 高敏感 (只回遮罩 + has_* 旗標)
  national_id_mask: string | null;
  salary_bank_name: string | null;
  salary_bank_branch: string | null;
  salary_account_holder: string | null;
  salary_account_mask: string | null;
  pii_updated_at: string | null;
  has_national_id: boolean;
  has_salary_account: boolean;
}

// 自助可改的欄位 (① 基本個資)
export type SelfProfilePatch = Partial<
  Pick<
    EmployeeProfile,
    | 'birth_date'
    | 'gender'
    | 'mobile_phone'
    | 'home_phone'
    | 'mailing_address'
    | 'registered_address'
    | 'emergency_contact_name'
    | 'emergency_contact_relation'
    | 'emergency_contact_phone'
  >
>;

// 人資可改的欄位 (① + ② + ③ 明文輸入)
export type HrProfilePatch = SelfProfilePatch &
  Partial<{
    hire_date: string | null;
    termination_date: string | null;
    termination_reason: string | null;
    department: string | null;
    manager_user_id: number | null;
    doc_exempt: boolean;
    doc_exempt_reason: string | null;
    national_id: string | null; // 明文 → 後端加密
    salary_bank_name: string | null;
    salary_bank_branch: string | null;
    salary_account_holder: string | null;
    salary_account: string | null; // 明文 → 後端加密
  }>;

// ---- 員工文件庫 (V273) ----
export interface EmployeeDoc {
  id: string;
  doc_type: string;
  filename: string;
  content_type: string | null;
  size_bytes: string | null;
  uploaded_by: string | null;
  uploader_name: string | null;
  uploaded_at: string;
  download_url: string;
}
export const DOC_TYPE_LABELS: Record<string, string> = {
  labor_contract: '勞動契約',
  offer: '錄取通知',
  id_doc: '身分證件',
  certificate: '證照',
  other: '其他',
};
export function useMemberDocuments(actor: DevActorPreset, userId: number | null) {
  return useQuery<EmployeeDoc[], ApiError>({
    queryKey: ['member-documents', userId, actor],
    queryFn: () => api<EmployeeDoc[]>(`/api/v1/members/${userId}/documents`, { actor }),
    enabled: !!userId,
  });
}

// ---- 勞健保投保異動 (V274) ----
export interface InsuranceEvent {
  id: string;
  event_type: string;
  effective_date: string;
  // V325: 勞保 / 健保 分兩欄; insured_salary 保留 (舊資料 / 稽核)
  labor_insured_salary: string | null;
  nhi_insured_salary: string | null;
  insured_salary: string | null;
  note: string | null;
  created_by_name: string | null;
  created_at: string;
}
export const INS_EVENT_LABELS: Record<string, string> = {
  enroll: '加保',
  withdraw: '退保',
  adjust: '調保',
};
export function useMemberInsuranceEvents(actor: DevActorPreset, userId: number | null) {
  return useQuery<InsuranceEvent[], ApiError>({
    queryKey: ['member-insurance-events', userId, actor],
    queryFn: () => api<InsuranceEvent[]>(`/api/v1/members/${userId}/insurance-events`, { actor }),
    enabled: !!userId,
  });
}

export function useMyProfile(actor: DevActorPreset) {
  return useQuery<EmployeeProfile | null, ApiError>({
    queryKey: ['my-profile', actor],
    queryFn: () => api<EmployeeProfile | null>('/api/v1/me/profile', { actor }),
  });
}

export function useUpdateMyProfile(actor: DevActorPreset) {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, SelfProfilePatch>({
    mutationFn: (patch) => api('/api/v1/me/profile', { actor, method: 'PATCH', json: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-profile'] }),
  });
}

export function useMemberProfile(actor: DevActorPreset, userId: number | null) {
  return useQuery<EmployeeProfile | null, ApiError>({
    queryKey: ['member-profile', userId, actor],
    queryFn: () => api<EmployeeProfile | null>(`/api/v1/members/${userId}/profile`, { actor }),
    enabled: userId != null,
  });
}

export function useUpdateMemberProfile(actor: DevActorPreset, userId: number) {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, HrProfilePatch>({
    mutationFn: (patch) =>
      api(`/api/v1/members/${userId}/profile`, { actor, method: 'PATCH', json: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['member-profile', userId] }),
  });
}

// ---- 離職 (offboarding, 2026-07-19) ----
//
// 後端把「會自動做的」跟「你得自己去做的」分開回傳 — 因為稽核發現填了離職日其實
// 什麼都不會發生, 而該做的事散在四個地方沒人列出來。
export interface OffboardingPreview {
  user: {
    id: string;
    name: string | null;
    email: string;
    is_active: boolean;
    termination_date: string | null;
  };
  will_revoke: {
    assignments: {
      resource_type: 'fund' | 'project';
      resource_id: string;
      role_code: string;
      resource_name: string | null;
    }[];
    delegations: {
      id: string;
      start_date: string;
      end_date: string;
      side: 'delegator' | 'delegate';
      counterpart_name: string | null;
    }[];
  };
  /** 指名由他簽的待簽單 — 停用後會卡死 */
  blocking_approvals: {
    id: string;
    request_no: string;
    request_type: string;
    title: string;
    amount: string | null;
    currency: string | null;
    seq: number;
    step_label: string | null;
  }[];
  in_flight_requests: { id: string; request_no: string; request_type: string; title: string }[];
  leave_balance: {
    year: number;
    entitled_days: string;
    used_days: string;
    remaining_days: string;
  } | null;
  manual_followups: string[];
}

export function useOffboardingPreview(actor: DevActorPreset, userId: number | null) {
  return useQuery<OffboardingPreview, ApiError>({
    queryKey: ['offboarding-preview', userId, actor],
    queryFn: () =>
      api<OffboardingPreview>(`/api/v1/members/${userId}/offboarding-preview`, { actor }),
    enabled: userId != null,
    staleTime: 0, // 每次開都要是最新的 — 這是要拿來做不可逆決定的
  });
}

export function useOffboard(actor: DevActorPreset, userId: number) {
  const qc = useQueryClient();
  return useMutation<
    { ok: true; revoked_assignments: number; revoked_delegations: number },
    ApiError,
    { termination_date: string; termination_reason?: string | null; force?: boolean }
  >({
    mutationFn: (body) =>
      api(`/api/v1/members/${userId}/offboard`, { actor, method: 'POST', json: body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['member-profile', userId] });
      qc.invalidateQueries({ queryKey: ['offboarding-preview', userId] });
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['user-resource-assignments'] });
    },
  });
}
